import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getPostBySlug, getPrevNext } from "../data/blogPosts";
import Button from "../components/ui/Button";
import Footer from "../components/Footer";

/** Share buttons for the blog post sidebar. */
function ShareButtons({ title, url }) {
  const [copied, setCopied] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = [
    {
      name: "LinkedIn",
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
    {
      name: "Facebook",
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    {
      name: "X (Twitter)",
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
      ),
    },
    {
      name: "WhatsApp",
      href: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
    },
  ];

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700 mt-4 lg:mt-6">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
        Share
      </h3>
      <div className="flex flex-wrap gap-2">
        {shareLinks.map(({ name, href, icon }) => (
          <a
            key={name}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            title={`Share on ${name}`}
            className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            {icon}
          </a>
        ))}
        <button
          type="button"
          onClick={handleCopyLink}
          title="Copy link"
          className="inline-flex items-center justify-center w-10 h-10 rounded-lg border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
          </svg>
        </button>
      </div>
      {copied && <p className="text-xs text-green-600 dark:text-green-400 mt-2">Link copied!</p>}
    </div>
  );
}

/** Article body for slug "5-website-checks-you-can-automate". Uses SiteScope Tailwind only. */
function PostContent5WebsiteChecks() {
  return (
    <>
      <div className="border-l-4 border-primary-400 dark:border-primary-300 bg-blue-50 dark:bg-gray-800/50 rounded-r-xl p-4 my-6 text-gray-900 dark:text-gray-100">
        If your website makes money, manual checking is negligence. Automate these 5 checks and you'll catch most failures before customers do.
      </div>

      <h2 id="uptime" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        1) Uptime monitoring (HTTP status + availability)
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        <strong className="text-gray-900 dark:text-gray-100">What it catches:</strong> server down, deploy failures, DNS issues.
      </p>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        <strong className="text-gray-900 dark:text-gray-100">Minimum setup:</strong>
      </p>
      <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 my-3 ml-2 space-y-1">
        <li>Ping every <strong className="text-gray-900 dark:text-gray-100">1–5 minutes</strong></li>
        <li>Mark down after <strong className="text-gray-900 dark:text-gray-100">2–3 consecutive failures</strong></li>
        <li>Alert on <strong className="text-gray-900 dark:text-gray-100">state change</strong> (UP→DOWN, DOWN→UP)</li>
      </ul>
      <p className="text-gray-500 dark:text-gray-400 text-sm my-3">
        Recommended defaults: interval <code className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-sm">2m</code>, confirm <code className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-sm">3 fails</code>, cooldown <code className="bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded px-1.5 py-0.5 text-sm">15m</code>.
      </p>

      <h2 id="ssl" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        2) SSL expiry monitoring (TLS certificate validity)
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        Alert at <strong className="text-gray-900 dark:text-gray-100">30 / 14 / 7 / 1 days</strong>. Also watch for hostname mismatch and invalid certificate chain.
      </p>

      <h2 id="domain" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        3) Domain expiry monitoring
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        Rare but catastrophic. Alert at <strong className="text-gray-900 dark:text-gray-100">60 / 30 / 14 / 7 days</strong>.
      </p>

      <h2 id="redirects" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        4) Redirect + critical URL monitoring
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        Monitor your money pages: homepage, pricing, contact, checkout/cart entry, top ad/SEO landing pages.
      </p>
      <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 my-3 ml-2 space-y-1">
        <li>Final status code (expect 200 or known redirect)</li>
        <li>Redirect loops / chains</li>
        <li>Final destination matches expectation</li>
      </ul>

      <h2 id="speed" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        5) Page speed monitoring
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        Speed issues are soft downtime. Track <strong className="text-gray-900 dark:text-gray-100">TTFB</strong> and baseline regression.
      </p>
      <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 my-3 ml-2 space-y-1">
        <li>TTFB target: <strong className="text-gray-900 dark:text-gray-100">&lt; 500ms</strong></li>
        <li>Alert if TTFB <strong className="text-gray-900 dark:text-gray-100">doubles</strong> for <strong className="text-gray-900 dark:text-gray-100">15–30 minutes</strong></li>
      </ul>

      <h2 id="checklist" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        Copy-paste checklist
      </h2>
      <ul className="text-gray-600 dark:text-gray-400 my-3 space-y-2">
        <li>✅ Uptime: ping every <strong className="text-gray-900 dark:text-gray-100">2 min</strong>, <strong className="text-gray-900 dark:text-gray-100">3-fail</strong> confirm, state-change alerts</li>
        <li>✅ SSL: alert at <strong className="text-gray-900 dark:text-gray-100">30/14/7/1 days</strong></li>
        <li>✅ Domain: alert at <strong className="text-gray-900 dark:text-gray-100">60/30/14/7 days</strong></li>
        <li>✅ URLs: monitor <strong className="text-gray-900 dark:text-gray-100">5–20</strong> critical pages</li>
        <li>✅ Speed: track <strong className="text-gray-900 dark:text-gray-100">TTFB + response time</strong>, alert on regression</li>
      </ul>

      <h2 id="cta" className="text-2xl font-bold text-gray-900 dark:text-gray-100 mt-8 mb-3">
        Ready to automate this?
      </h2>
      <p className="text-gray-600 dark:text-gray-400 my-3">
        If you want these checks in one dashboard with clean alerts, set up monitoring in minutes.
      </p>
      <div className="flex flex-wrap gap-3 items-center mt-6">
        <Button as={Link} to="/pricing" variant="primary" size="md" light>
          Try SiteScope
        </Button>
        <Button as={Link} to="/why-sitescope" variant="outline" size="md" light>
          See how monitoring works
        </Button>
      </div>
    </>
  );
}

/** Returns the content component for a given slug. Add new posts here. */
function getPostContent(slug) {
  switch (slug) {
    case "5-website-checks-you-can-automate":
      return <PostContent5WebsiteChecks />;
    default:
      return null;
  }
}

const TOC_ITEMS = [
  { id: "uptime", label: "1) Uptime monitoring" },
  { id: "ssl", label: "2) SSL expiry" },
  { id: "domain", label: "3) Domain expiry" },
  { id: "redirects", label: "4) Redirects + critical URLs" },
  { id: "speed", label: "5) Page speed" },
  { id: "checklist", label: "Checklist" },
  { id: "cta", label: "CTA" },
];

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const { prev, next } = getPrevNext(slug || "");
  const content = getPostContent(slug);
  const shareUrl =
    typeof window !== "undefined" ? window.location.origin + window.location.pathname : "";

  if (!post || !content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
        <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 w-full">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
              <img src="/logo-icon.png" alt="SiteScope" className="w-12 h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105" />
              <span className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">SiteScope</span>
            </Link>
            <Link to="/blog" className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
              ← Back to Blog
            </Link>
          </div>
        </header>
        <main className="flex-grow max-w-4xl mx-auto px-4 sm:px-6 py-12 w-full">
          <p className="text-gray-500 dark:text-gray-400">Post not found.</p>
          <Link to="/blog" className="inline-block mt-4 text-blue-600 dark:text-blue-400 hover:underline">
            Back to Blog
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
      <header className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-6 w-full">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 group">
            <img
              src="/logo-icon.png"
              alt="SiteScope"
              className="w-12 h-12 sm:w-12 sm:h-12 md:w-14 md:h-14 transition-transform group-hover:scale-105"
            />
            <span className="text-2xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100">
              SiteScope
            </span>
          </Link>
          <Link
            to="/blog"
            className="text-sm sm:text-base font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            ← Back to Blog
          </Link>
        </div>
      </header>

      <main className="flex-grow w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <header className="bg-white dark:bg-gray-800 rounded-2xl overflow-hidden border border-gray-200 dark:border-gray-700 mb-8">
          {post.image && (
            <div className="w-full h-[200px] bg-gray-200 dark:bg-gray-700 overflow-hidden flex-shrink-0">
              <img
                src={post.image}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <div className="p-6 sm:p-8">
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
              {post.category || "Website Monitoring"}
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100 mt-2 mb-2">
              {post.title}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 mt-0 mb-4">
              {post.excerpt}
            </p>
            <div className="flex flex-wrap gap-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                {post.readingTime}
              </span>
              <span className="px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                Updated: {post.date}
              </span>
              <span className="px-3 py-1 rounded-full border border-gray-200 dark:border-gray-600">
                By {post.author || "SiteScope"}
              </span>
            </div>
          </div>
        </header>

        <div className="grid grid-cols-12 gap-4 sm:gap-6 lg:gap-8">
          <aside className="col-span-12 lg:col-span-3 order-2 lg:order-1 lg:sticky lg:top-6 lg:self-start space-y-4 lg:space-y-6">
            <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border border-gray-200 dark:border-gray-700">
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
                On this page
              </h3>
              <nav className="flex flex-col gap-0.5">
                {TOC_ITEMS.map((item) => (
                  <a
                    key={item.id}
                    href={`#${item.id}`}
                    className="block py-2 px-2 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-sm"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
            <ShareButtons title={post.title} url={shareUrl} />
          </aside>

          <article className="col-span-12 lg:col-span-9 order-1 lg:order-2 bg-white dark:bg-gray-800 rounded-2xl p-6 sm:p-8 border border-gray-200 dark:border-gray-700">
            {content}

            <footer className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700 text-sm text-gray-500 dark:text-gray-400">
              <div className="flex flex-wrap gap-4 justify-between items-center">
                {prev ? (
                  <Link
                    to={`/blog/${prev.slug}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    ← {prev.title}
                  </Link>
                ) : (
                  <span />
                )}
                {next ? (
                  <Link
                    to={`/blog/${next.slug}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                  >
                    {next.title} →
                  </Link>
                ) : (
                  <span />
                )}
              </div>
            </footer>
          </article>
        </div>
      </main>

      <Footer />
    </div>
  );
}
